import { SuccessToast } from '@strata/react-toast-message';
import { isTaskTodo, Worklist, type WorklistTask } from '@strata/react-worklist';
import { type ComponentProps, useCallback } from 'react';
import { type Location, NavigationType, useLocation, useNavigate } from 'react-router';

import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { useAppDispatch } from 'store';
import {
    usePostDoNotConvertMutation,
    usePostUnableToCompleteTaskMutation,
} from 'store/api/worklistApi';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

/** Show dialog if coming from worklist page */
export const showDialogForWorklistTypes = ({
    types,
    navigationType,
    location,
}: {
    types: (typeof Worklist.WorkTaskType)[keyof typeof Worklist.WorkTaskType][];
    navigationType: NavigationType;
    location: Location<{ worklistTask?: WorklistTask }>;
}) =>
    !!location.state?.worklistTask?.task_type &&
    types.includes(location.state.worklistTask.task_type) &&
    // Ensure dialog is only shown once (initial overview tab & not back navigation)
    navigationType !== NavigationType.Pop;

const backToWorklist = {
    backToDestination: {
        route: AppRoutes.worklist,
        text: 'Worklist',
    },
};

const referralTaskTypes: WorklistTask['task_type'][] = [
    Worklist.WorkTaskType.PENDINTAKE,
    Worklist.WorkTaskType.ACCEPTANCE,
    Worklist.WorkTaskType.REBILL,
    Worklist.WorkTaskType.MANSUBTASK,
    Worklist.WorkTaskType.NBD,
    Worklist.WorkTaskType.CONVERSION,
];

export const useOnTaskActionClick = (fromWorklistPage = false) => {
    const navigate = useNavigate();
    const location = useLocation() as Location<{
        worklistTask?: WorklistTask;
        fromWorklistPage?: boolean;
    }>;
    return useCallback<Required<ComponentProps<typeof Worklist>>['onTaskActionClick']>(
        (worklistTask, taskAction) => {
            // Do not convert and block stay on the worklist. Convert follows the other referral tasks.
            if (
                worklistTask.task_type === Worklist.WorkTaskType.CONVERSION &&
                taskAction !== Worklist.WorklistTaskAction.CONVERT
            ) {
                return;
            }
            if (referralTaskTypes.includes(worklistTask.task_type)) {
                if (!isTaskTodo(worklistTask)) {
                    navigate(AppRoutes.wonReferralDetails(worklistTask.referral_id), {
                        state: { ...backToWorklist },
                    });
                } else {
                    navigate(
                        {
                            pathname: AppRoutes.wonReferralDetails(worklistTask.referral_id),
                            hash:
                                worklistTask.task_type === Worklist.WorkTaskType.MANSUBTASK
                                    ? 'performance'
                                    : location.hash,
                        },
                        {
                            state: {
                                ...backToWorklist,
                                ...location.state,
                                worklistTask,
                                fromWorklistPage,
                            },
                        },
                    );
                }
            }
        },
        [fromWorklistPage, location, navigate],
    );
};

export const useOnDoNotConvert = () => {
    const dispatch = useAppDispatch();
    const writeWorklist = usePermissionCheck(Permissions.WRITE_WORKLIST);
    const [postDoNotConvert] = usePostDoNotConvertMutation();
    return useCallback(
        (worklistTask: WorklistTask) => {
            if (!writeWorklist) {
                return;
            }
            void (async () => {
                await postDoNotConvert({ taskId: worklistTask.task_id }).unwrap();
                await dispatch(fetchWorklistTasks());
                SuccessToast({ message: 'Task marked as did not convert.' });
            })();
        },
        [writeWorklist, postDoNotConvert, dispatch],
    );
};

export const useOnUnableToComplete = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [postUnableToCompleteTask] = usePostUnableToCompleteTaskMutation();
    return useCallback<Required<ComponentProps<typeof Worklist>>['onUnableToComplete']>(
        ({ task, reason, beginSupportConversation }) => {
            void (async () => {
                await postUnableToCompleteTask({ taskId: task.task_id, reason }).unwrap();
                await dispatch(fetchWorklistTasks());
                SuccessToast({ message: 'Task marked as unable to complete.' });
                if (beginSupportConversation) {
                    navigate(
                        {
                            pathname: AppRoutes.wonReferralDetails(task.referral_id),
                            hash: 'messages',
                        },
                        { state: { worklistTask: task, beginSupportConversation } },
                    );
                }
            })();
        },
        [postUnableToCompleteTask, dispatch, navigate],
    );
};

export const useReturnToWorklistCheck = () => {
    const navigate = useNavigate();
    const location = useLocation() as Location<{
        worklistTask?: WorklistTask;
        fromWorklistPage?: boolean;
    }>;

    return useCallback(() => {
        // If completing from worklist task, navigate back afterwards
        if (location.state?.worklistTask) {
            navigate(
                // Navigate to either worklist page or referral worklist tab
                location.state.fromWorklistPage
                    ? AppRoutes.worklist
                    : { ...location, hash: 'worklist' },
            );
        }
    }, [navigate, location]);
};
